import { IBackend } from '@/api/BackendApi';
import { nodeTypeSupportsFiles, nodeTypeSupportsNotes } from '@/lib/tree/treeNodeTypeConfigs';
import { useTreeStore } from '@/stores/treeStore';
import { log } from '@/lib/utils/logger';

/** MUI TreeView applies expandedItems only after TreeItem children are committed to the DOM. */
function expandNodeWhenReady(nodeId: string, afterChildrenLoad: boolean): void {
  const runExpand = () => {
    const current = useTreeStore.getState();
    const node = current.nodes.get(nodeId);
    if (node?.isExpandable && !current.expandedNodes.has(nodeId)) {
      current.expandNode(nodeId);
    }
  };

  if (afterChildrenLoad) {
    requestAnimationFrame(() => requestAnimationFrame(runExpand));
    return;
  }

  runExpand();
}

async function ensureNotesAndFiles(nodeId: string, backend: IBackend): Promise<void> {
  const store = useTreeStore.getState();
  const node = store.nodes.get(nodeId);
  if (!node || node.needsDataReload) {
    return;
  }

  if (nodeTypeSupportsNotes(node.type) && !store.isNotesLoaded(node.dataId)) {
    await store.loadNotes(node.dataId, backend);
  }
  if (nodeTypeSupportsFiles(node.type) && !store.isFilesLoaded(node.dataId)) {
    await store.loadFiles(node.dataId, backend);
  }
}

/**
 * Select a tree node after reloading lightweight entity data when required.
 * Project/data-package sample list endpoints return partial DTOs (no projectLabels);
 * this must run before the detail pane reads from the store.
 *
 * Does not expand or load children unless options request it (mouse click uses expand via
 * BaseTreeArea for containers; chevron uses handleNodeToggle).
 */
export async function selectTreeNode(
  nodeId: string,
  backend: IBackend,
  options?: { loadChildren?: boolean; expandIfExpandable?: boolean }
): Promise<void> {
  const loadChildren = options?.loadChildren === true;
  const expandIfExpandable = options?.expandIfExpandable === true;
  const store = useTreeStore.getState();
  const node = store.nodes.get(nodeId);
  if (!node) {
    return;
  }

  if (node.needsDataReload) {
    log.debug('[selectTreeNode] Reloading lightweight node before selection:', { nodeId });
    try {
      await store.reloadNode(nodeId, backend);
    } catch (error) {
      log.error('[selectTreeNode] Failed to reload node before selection:', error);
    }
  } else {
    await ensureNotesAndFiles(nodeId, backend);
  }

  const didLoadChildren = loadChildren && !nodeId.startsWith('shippedsample-') && !store.areChildrenLoaded(nodeId);

  if (didLoadChildren) {
    await store.loadChildren(nodeId, backend);
  }

  store.setSelectedNode(nodeId);

  if (expandIfExpandable && !nodeId.startsWith('shippedsample-')) {
    expandNodeWhenReady(nodeId, didLoadChildren);
  }
}
